<template>
  <v-card height="100%">
    <v-card-title>
      <v-container class="panel">
        <v-layout row
                  wrap>
          <v-flex xs12>
            <v-layout column
                      class="panel-heading">
              <v-flex d-flex>
                <h2 class="panel-title">
                  <b>
                    <span v-html="$t('private_dash_table_bookings_title')"></span>
                  </b>
                  <router-link :to=" {name: 'ParkingActivity', params: { id: parkingId} }"
                               v-if="parkingId">
                    <v-icon color="primary"
                            class="right">edit</v-icon>
                  </router-link>
                </h2>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex xs12>
            <span v-html="$t('private_dash_table_earnings_nodata')"
                  v-if="isEmpty"></span>
            <div ref="chart"
                 v-else>
              <chart v-for="data in graphs"
                     :key="data.id"
                     :datasets="data.datasets"
                     :bar-options="data.barOptions"
                     :height="chartHeight"></chart>
            </div>
          </v-flex>
        </v-layout>
      </v-container>
    </v-card-title>
  </v-card>
</template>

<script>
import FromDateSelector from '@/components/commons/FromToDateSelector.vue';
import statisticService from '@/services/statisticService';
import Chart from '@/components/commons/charts/Chart.vue';
import { chartsHelpers } from '@/components/commons/charts/chartsHelpers';
import moment from 'moment';
import _ from 'lodash';
import keys from '@/store/keys';
import { EventBus } from '@/services/eventBus';
import store from '@/store';

export default {
  name: 'booking-graph-box',
  components: {
    [FromDateSelector.name]: FromDateSelector,
    [Chart.name]: Chart
  },
  props: ['parkingId'],
  data() {
    return {
      dates: [
        moment()
          .startOf('month')
          .toDate(),
        moment()
          .endOf('month')
          .toDate()
      ],
      graphs: [],
      isEmpty: false,
      plotUnit: 'day',
      chartHeight: 600
    };
  },
  created() {
    this.initializeGraph();
    EventBus.$on(keys.eventOnLanguageChanged, this.updateBookings);
    EventBus.$on(keys.eventOnScreenSizeChanged, this.refreshGraph);

    let displaySize = chartsHelpers.getDisplaySize(this);

    if (displaySize === 'xs') {
      this.chartHeight = 600;
    } else {
      this.chartHeight = 250;
    }
  },
  destroyed() {
    EventBus.$off(keys.eventOnLanguageChanged, this.updateBookings);
    EventBus.$off(keys.eventOnScreenSizeChanged, this.refreshGraph);
  },
  methods: {
    initializeGraph() {
      this.graphs = [
        {
          datasets: {
            labels: this.dates,
            datasets: []
          },
          barOptions: chartsHelpers.getBarBookingOptions(this),
          id: _.uniqueId()
        }
      ];
    },
    async refreshGraph(params) {
      if (params.to === 'xs') {
        this.chartHeight = 600;
      } else {
        this.chartHeight = 250;
      }
      if (typeof this.$refs.chart === 'undefined') {
        return;
      }
      this.$refs.chart.getElementsByTagName('canvas')[0].style.width = 0;
      await this.updateBookings(this.dates);
    },
    async updateBookings(dates) {
      if (typeof dates === 'undefined') {
        if (this.dates.length === 0) {
          return;
        }
      } else {
        this.dates = _.clone(dates);
      }
      let params = {
        start: moment(this.dates[0]).format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
        end: moment(this.dates[1]).format('YYYY-MM-DD[T]HH:mm:ss[Z]')
      };

      if (typeof this.parkingId !== 'undefined') {
        params.parking = this.parkingId;
      }

      await statisticService.bookings(params, this.bookingsSuccessCallback, this.bookingsErrorCallback);
    },
    bookingsSuccessCallback(data) {
      if (typeof data.chartData === 'undefined' || data.chartData.length <= 0) {
        EventBus.$emit(keys.eventOnBookingGraphDone, { done: true });
        this.isEmpty = true;
        return;
      }
      this.$nextTick(() => {
        this.graphs = [];
        this.isEmpty = false;

        this.plotUnit = chartsHelpers.getPlotUnit(this.dates[0], this.dates[1]);

        let dates = [];
        let bookings = [];
        let subscriptions = [];
        let tenants = [];
        let totals = [];

        dates.push(moment(this.dates[0]));
        bookings.push(0);
        subscriptions.push(0);
        tenants.push(0);
        totals.push(0);

        for (let i = 0; i < data.chartData.length; i++) {
          dates.push(moment(data.chartData[i].date, 'YYYY/MM/DD'));
          bookings.push(data.chartData[i].bookings.toFixed(2));
          subscriptions.push(data.chartData[i].subscriptions.toFixed(2));
          tenants.push(data.chartData[i].tenants.toFixed(2));
          totals.push((data.chartData[i].bookings + data.chartData[i].subscriptions + data.chartData[i].tenants).toFixed(2));
        }

        let lastDate = moment(_.last(data.chartData).date, 'YYYY/MM/DD').add({
          days: 1
        });
        if (lastDate.isAfter(moment(this.dates[1]))) {
          lastDate = moment(this.dates[1]);
        }
        dates.push(lastDate);
        bookings.push(0);
        subscriptions.push(0);
        tenants.push(0);
        totals.push(0);

        let localeIso = store.state.user.manager.localeIso;

        let yTickMax = _.maxBy(totals, itTotal => {
          return Number(itTotal);
        });

        let yTickMaxLength = Math.trunc(yTickMax).toString().length;
        let yTicketFirstCharacter = Math.trunc(yTickMax).toString()[0];
        let yTicketFirstNumber = Number(yTicketFirstCharacter) + 1;
        let yTickMaxLimit = Number(yTicketFirstNumber.toString().padEnd(yTickMaxLength, '0'));
        if (yTicketFirstCharacter === '9') {
          yTickMaxLimit = Number(yTicketFirstNumber.toString().padEnd(yTickMaxLength + 1, '0'));
        }

        let barOptions = chartsHelpers.getBarBookingOptions(this, localeIso, yTickMaxLimit);

        let datasets = {
          labels: dates,
          datasets: [
            {
              yAxisID: 'stacked',
              label: 'private_dash_table_incomes_legend_tennants',
              type: 'line',
              backgroundColor: 'rgba(220,220,220,0.75)',
              borderColor: 'rgba(220,220,220,0)',
              hoverBackgroundColor: 'rgba(220,220,220,0.95)',
              hoverBorderColor: 'rgba(220,220,220,0)',
              data: tenants,
              steppedLine: true
            },
            {
              yAxisID: 'stacked',
              label: 'private_dash_table_incomes_legend_subscriptions',
              type: 'line',
              backgroundColor: 'rgba(255, 202, 40,0.75)',
              borderColor: 'rgba(255, 202, 40,0)',
              hoverBackgroundColor: 'rgba(255, 202, 40,0.95)',
              hoverBorderColor: 'rgba(139, 195, 74,0)',
              data: subscriptions,
              steppedLine: true
            },
            {
              yAxisID: 'stacked',
              label: 'private_dash_table_incomes_legend_bookings',
              type: 'line',
              backgroundColor: 'rgba(54, 198, 211,0.75)',
              borderColor: 'rgba(54, 198, 211,0)',
              hoverBackgroundColor: 'rgba(54, 198, 211,0.95)',
              hoverBorderColor: 'rgba(54, 198, 211,0)',
              data: bookings,
              steppedLine: true
            },
            {
              yAxisID: 'non-stacked',
              label: 'private_dash_table_incomes_legend_total',
              type: 'line',
              backgroundColor: 'rgba(211, 198, 211,0)',
              borderColor: 'rgba(54, 54, 211,0.3)',
              hoverBackgroundColor: 'rgba(54, 54, 211,0)',
              hoverBorderColor: 'rgba(54, 54, 211,0.8)',
              data: totals,
              steppedLine: true
            }
          ]
        };

        this.graphs.push({
          datasets: datasets,
          barOptions: barOptions,
          id: _.uniqueId()
        });
      });

      EventBus.$emit(keys.eventOnBookingGraphDone, { done: true });
    },
    bookingsErrorCallback(data) {}
  }
};
</script>

<style lang="scss" scoped>
</style>



// WEBPACK FOOTER //
// src/components/commons/BookingGraphBox.vue